import * as React from 'react';
import { useEffect, useState } from 'react';
import { Button, PageSection, Progress, ProgressMeasureLocation, ProgressSize, Spinner, Tooltip } from '@patternfly/react-core';
import {
    BoltIcon,
    CheckCircleIcon,
    CrownIcon,
    ExclamationTriangleIcon,
    OutlinedClockIcon,
    StarIcon
} from '@patternfly/react-icons';
import {
    ActivationError,
    SubscriptionResponse,
    SubscriptionTier,
    TierResponse,
    TrialOfferResponse,
    UpgradePreviewResponse,
    tierRank
} from '@app/Subscription/types/subscription';
import {
    CheckoutPaymentResponse,
    cancelSubscription,
    changeSubscription,
    createSubscriptionCheckout,
    getSubscription,
    getTiers,
    getTrialOffer,
    previewChange,
    reactivateSubscription
} from '@app/utils/utils';
import { SubscribePaymentModal } from '@app/Subscription/SubscribePaymentModal';
import { TopUpModal } from '@app/Subscription/TopUpModal';
import { TopUpContext } from '@app/Subscription/topUpUtils';
import { PaymentMethodsSection } from '@app/Subscription/PaymentMethodsSection';
import { InvoicesSection } from '@app/Subscription/InvoicesSection';

interface ConfirmModalProps {
    title: string;
    body: React.ReactNode;
    confirmLabel: string;
    onConfirm: () => void;
    onCancel: () => void;
    busy?: boolean;
    danger?: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
    title,
    body,
    confirmLabel,
    onConfirm,
    onCancel,
    busy,
    danger
}) => {
    const overlayStyle: React.CSSProperties = {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '24px'
    };
    const modalStyle: React.CSSProperties = {
        background: '#ffffff',
        borderRadius: '16px',
        maxWidth: '440px',
        width: '100%',
        padding: '28px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)'
    };
    return (
        <div style={overlayStyle} onClick={busy ? undefined : onCancel}>
            <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '20px', fontWeight: 700, color: '#1f2937' }}>
                    {title}
                </h3>
                <div style={{ color: '#4b5563', fontSize: '14px', lineHeight: 1.6, marginBottom: '24px' }}>
                    {body}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <Button variant="link" onClick={onCancel} isDisabled={busy}>
                        Cancel
                    </Button>
                    <Button
                        variant={danger ? 'danger' : 'primary'}
                        onClick={onConfirm}
                        isLoading={busy}
                        isDisabled={busy}
                    >
                        {confirmLabel}
                    </Button>
                </div>
            </div>
        </div>
    );
};

function formatDate(iso: string | null | undefined): string {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    } catch {
        return iso;
    }
}

function daysUntil(iso: string | null | undefined): number {
    if (!iso) return 0;
    const ms = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

function formatPrice(cents: number): string {
    return '$' + (cents / 100).toFixed(0);
}

// Cents → "$12.34" (2 decimals) for prorated amounts.
function formatMoney(cents: number): string {
    return '$' + (cents / 100).toFixed(2);
}

interface TierTheme {
    accent: string;
    gradient: string;
    softBg: string;
    border: string;
    shadow: string;
}

function tierTheme(tier: SubscriptionTier): TierTheme {
    switch (tier) {
        case 'BRONZE':
            return {
                accent: '#92400e',
                gradient: 'linear-gradient(135deg, #d4a574 0%, #92400e 100%)',
                softBg: 'linear-gradient(135deg, #fef3e2 0%, #fde4c4 100%)',
                border: '#cd7f32',
                shadow: '0 8px 28px rgba(180, 83, 9, 0.25)'
            };
        case 'SILVER':
            return {
                accent: '#475569',
                gradient: 'linear-gradient(135deg, #e2e8f0 0%, #64748b 100%)',
                softBg: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                border: '#94a3b8',
                shadow: '0 8px 28px rgba(100, 116, 139, 0.25)'
            };
        case 'GOLD':
            return {
                accent: '#a16207',
                gradient: 'linear-gradient(135deg, #fde047 0%, #ca8a04 100%)',
                softBg: 'linear-gradient(135deg, #fffbeb 0%, #fef08a 100%)',
                border: '#eab308',
                shadow: '0 8px 28px rgba(234, 179, 8, 0.32)'
            };
    }
}

const PlanComponent: React.FunctionComponent = () => {
    const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null);
    const [tiers, setTiers] = useState<TierResponse[]>([]);
    const [trialOffer, setTrialOffer] = useState<TrialOfferResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [refreshTick, setRefreshTick] = useState(0);

    // Confirm-modal state
    const [pendingChange, setPendingChange] = useState<SubscriptionTier | null>(null);
    const [preview, setPreview] = useState<UpgradePreviewResponse | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [confirmingCancel, setConfirmingCancel] = useState(false);
    const [busy, setBusy] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);

    // In-app payment modal state (Stripe Elements + PaymentElement)
    const [payment, setPayment] = useState<{ checkout: CheckoutPaymentResponse; tierLabel: string } | null>(null);
    // Set true after Stripe confirms payment; we poll /subscription until the webhook lands.
    const [postPaymentPolling, setPostPaymentPolling] = useState(false);

    // One-off credit top-up modal (null = closed)
    const [topUp, setTopUp] = useState<TopUpContext | null>(null);
    // Banner shown after returning from a 3DS redirect on a top-up payment.
    const [topUpConfirmed, setTopUpConfirmed] = useState(false);

    // If we got redirected back from Stripe (3DS / iDEAL flows or a stale Checkout link),
    // ?checkout=success means "the payment confirmed; webhook is on the way". Poll once on mount.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('checkout') === 'success') {
            // Clean the URL so a manual refresh doesn't re-trigger this branch.
            window.history.replaceState({}, '', window.location.pathname);
            handlePaymentSuccess();
        } else if (params.get('checkout') === 'topup-success') {
            // Top-up payment confirmed via redirect (3DS); credits land via webhook.
            window.history.replaceState({}, '', window.location.pathname);
            setTopUpConfirmed(true);
            refresh();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        Promise.all([getSubscription(), getTiers(), getTrialOffer()])
            .then(([sub, tierList, offer]) => {
                if (cancelled) return;
                setSubscription(sub);
                setTiers(tierList);
                setTrialOffer(offer);
                setLoadError(null);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error('Failed to load plan:', err);
                setLoadError('Unable to load subscription data.');
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [refreshTick]);

    const refresh = () => setRefreshTick((t) => t + 1);

    const hasActiveSub = !!subscription && (subscription.status === 'ACTIVE' || subscription.status === 'TRIALING');
    // The backend always returns a SubscriptionResponse, but `tier` is null for users who
    // haven't completed checkout yet (placeholder INCOMPLETE row). Use this to gate the
    // Current Plan card so new users don't see Bronze pre-selected.
    const hasSubscription = !!subscription && subscription.tier !== null;

    const tierDisplay = (t: SubscriptionTier): string => {
        const cfg = tiers.find((x) => x.tier === t);
        return cfg ? cfg.displayName : t.charAt(0) + t.slice(1).toLowerCase();
    };

    const handleStartTrial = async () => {
        if (!trialOffer) return;
        setBusy(true);
        setActionError(null);
        try {
            const checkout = await createSubscriptionCheckout(trialOffer.tier, true);
            setPayment({ checkout, tierLabel: tierDisplay(trialOffer.tier) });
        } catch (err: any) {
            setActionError(err.message || 'Failed to start trial.');
        } finally {
            setBusy(false);
        }
    };

    const handleSubscribe = async (tier: SubscriptionTier) => {
        setBusy(true);
        setActionError(null);
        try {
            const checkout = await createSubscriptionCheckout(tier, false);
            setPayment({ checkout, tierLabel: tierDisplay(tier) });
        } catch (err: any) {
            setActionError(err.message || 'Failed to start checkout.');
        } finally {
            setBusy(false);
        }
    };

    // Stripe confirmed payment client-side. Stripe then fires multiple webhooks in sequence —
    // customer.subscription.created lands first with status=INCOMPLETE, then
    // customer.subscription.updated flips to ACTIVE once the payment is recorded. Poll until we
    // see ACTIVE/TRIALING so the FE doesn't snapshot the transient INCOMPLETE state.
    const handlePaymentSuccess = async () => {
        setPostPaymentPolling(true);
        const start = Date.now();
        const TIMEOUT_MS = 15000;
        const INTERVAL_MS = 1000;
        let latest: SubscriptionResponse | null = null;
        while (Date.now() - start < TIMEOUT_MS) {
            try {
                const sub = await getSubscription();
                if (sub) {
                    latest = sub;
                    if (sub.status === 'ACTIVE' || sub.status === 'TRIALING') {
                        setSubscription(sub);
                        setPayment(null);
                        setPostPaymentPolling(false);
                        return;
                    }
                }
            } catch (err) {
                console.warn('poll /subscription failed:', err);
            }
            await new Promise((r) => setTimeout(r, INTERVAL_MS));
        }
        // Timed out — close modal and refresh; show whatever we last saw so the user isn't stuck.
        if (latest) setSubscription(latest);
        setPayment(null);
        setPostPaymentPolling(false);
        refresh();
    };

    // Open the confirm modal. For an upgrade, also fetch the exact prorated charge to show.
    const startChange = async (tier: SubscriptionTier) => {
        setActionError(null);
        setPreview(null);
        setPendingChange(tier);
        if (!subscription?.tier || tierRank(tier) <= tierRank(subscription.tier)) {
            return; // downgrade — no preview needed
        }
        setPreviewLoading(true);
        try {
            setPreview(await previewChange(tier));
        } catch (err: any) {
            setActionError(err.message || 'Could not calculate the upgrade cost.');
        } finally {
            setPreviewLoading(false);
        }
    };

    const closeChange = () => {
        setPendingChange(null);
        setPreview(null);
        setActionError(null);
    };

    const handleConfirmChange = async () => {
        if (!pendingChange) return;
        setBusy(true);
        setActionError(null);
        try {
            await changeSubscription(pendingChange, preview?.prorationDate);
            closeChange();
            refresh();
        } catch (err: any) {
            if (err instanceof ActivationError && err.body.reason === 'over_target_quota' && subscription) {
                setActionError(
                    `You've used ${subscription.credits.used} credits this period; downgrading to ${pendingChange} is blocked until next cycle. Try after ${formatDate(subscription.currentPeriodEnd)}.`
                );
            } else if (err instanceof ActivationError && err.body.reason === 'no_payment_method') {
                setActionError(
                    'No payment method on file. Add a card in the Payment methods section below, then try again.'
                );
            } else if (err instanceof ActivationError && err.body.reason === 'upgrade_charge_failed') {
                setActionError(
                    `We couldn't charge your card, so you're still on ${subscription?.tier}. Update your payment method and try again.`
                );
            } else {
                setActionError(err.message || 'Failed to update subscription.');
            }
        } finally {
            setBusy(false);
        }
    };

    const handleCancel = async () => {
        setBusy(true);
        setActionError(null);
        try {
            await cancelSubscription();
            setConfirmingCancel(false);
            refresh();
        } catch (err: any) {
            setActionError(err.message || 'Failed to cancel subscription.');
        } finally {
            setBusy(false);
        }
    };

    const handleReactivate = async () => {
        setBusy(true);
        setActionError(null);
        try {
            await reactivateSubscription();
            refresh();
        } catch (err: any) {
            setActionError(err.message || 'Failed to reactivate subscription.');
        } finally {
            setBusy(false);
        }
    };

    // Styles
    const styles: Record<string, React.CSSProperties> = {
        container: {
            padding: '24px',
            maxWidth: '1080px',
            margin: '0 auto'
        },
        header: {
            marginBottom: '24px'
        },
        title: {
            fontSize: '28px',
            fontWeight: 700,
            color: '#1f2937',
            margin: 0
        },
        subtitle: {
            fontSize: '14px',
            color: '#6b7280',
            marginTop: '4px'
        },
        sectionCard: {
            background: '#ffffff',
            borderRadius: '14px',
            padding: '24px',
            border: '1px solid #e5e7eb',
            marginBottom: '20px'
        },
        sectionTitle: {
            fontSize: '14px',
            fontWeight: 600,
            color: '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '14px'
        },
        trialBanner: {
            background: 'linear-gradient(135deg, #f59e0b 0%, #b45309 100%)',
            color: '#ffffff',
            borderRadius: '14px',
            padding: '20px 24px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap'
        },
        badge: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 12px',
            borderRadius: '999px',
            fontSize: '12px',
            fontWeight: 600
        },
        usageRow: {
            marginBottom: '18px'
        },
        usageLabel: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            fontSize: '13px',
            color: '#4b5563',
            marginBottom: '6px'
        },
        tierGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '16px',
            marginTop: '16px'
        },
        tierCard: {
            position: 'relative',
            borderRadius: '14px',
            padding: '28px 20px 24px',
            background: '#ffffff',
            border: '2px solid #e5e7eb',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            overflow: 'hidden',
            transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease'
        },
        tierStripe: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '8px'
        },
        tierIconCircle: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            color: '#ffffff',
            fontSize: '14px',
            flexShrink: 0,
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.12)'
        },
        tierName: {
            fontSize: '18px',
            fontWeight: 700,
            color: '#1f2937',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        tierPrice: {
            fontSize: '28px',
            fontWeight: 700,
            color: '#1f2937'
        },
        tierPriceSuffix: {
            fontSize: '13px',
            color: '#6b7280',
            fontWeight: 500
        },
        featureList: {
            listStyle: 'none',
            padding: 0,
            margin: '4px 0 12px 0',
            color: '#374151',
            fontSize: '13px',
            lineHeight: 1.7
        },
        errorBanner: {
            background: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#991b1b',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '14px'
        }
    };

    if (isLoading) {
        return (
            <PageSection style={{ minHeight: '100%', background: '#f8fafc' }}>
                <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 20px' }}>
                    <Spinner size="xl" />
                </div>
            </PageSection>
        );
    }

    if (loadError) {
        return (
            <PageSection style={{ minHeight: '100%', background: '#f8fafc' }}>
                <div style={styles.container}>
                    <div style={styles.errorBanner}>{loadError}</div>
                </div>
            </PageSection>
        );
    }

    const showTrialBanner = !!trialOffer && !hasActiveSub;
    const currentTier: SubscriptionTier | null = subscription ? subscription.tier : null;
    const trialDaysLeft = subscription && subscription.status === 'TRIALING'
        ? daysUntil(subscription.trialEnd)
        : 0;

    const renderStatusBadge = (sub: SubscriptionResponse) => {
        if (sub.cancelAtPeriodEnd) {
            return (
                <span style={{ ...styles.badge, background: '#fef3c7', color: '#92400e' }}>
                    <OutlinedClockIcon /> Canceling on {formatDate(sub.currentPeriodEnd)}
                </span>
            );
        }
        switch (sub.status) {
            case 'ACTIVE':
                return (
                    <span style={{ ...styles.badge, background: '#dcfce7', color: '#166534' }}>
                        <CheckCircleIcon /> Active
                    </span>
                );
            case 'TRIALING':
                return (
                    <span style={{ ...styles.badge, background: '#dbeafe', color: '#1e40af' }}>
                        <StarIcon /> Trialing — {trialDaysLeft}d left
                    </span>
                );
            case 'PAST_DUE':
                return (
                    <span style={{ ...styles.badge, background: '#fee2e2', color: '#991b1b' }}>
                        <ExclamationTriangleIcon /> Past Due
                    </span>
                );
            case 'CANCELED':
                return (
                    <span style={{ ...styles.badge, background: '#f3f4f6', color: '#4b5563' }}>
                        Canceled
                    </span>
                );
            case 'INCOMPLETE':
                return (
                    <span style={{ ...styles.badge, background: '#fef3c7', color: '#92400e' }}>
                        Incomplete
                    </span>
                );
        }
    };

    const renderUsageBars = (sub: SubscriptionResponse) => {
        const aiLimit = sub.aiAds.limit;
        const aiUnlimited = aiLimit === null;
        const aiNotIncluded = aiLimit === 0;
        return (
            <div>
                <div style={styles.usageRow}>
                    <div style={styles.usageLabel}>
                        <Tooltip content="Route-based campaigns count as 2 credits; target-reach campaigns count as 3">
                            <span>
                                {sub.credits.used} / {sub.credits.limit} campaign credits used
                                {(sub.credits.purchased ?? 0) > 0 && (
                                    <span style={{ color: '#059669', fontWeight: 600 }}> · +{sub.credits.purchased} purchased</span>
                                )}
                            </span>
                        </Tooltip>
                    </div>
                    <Progress
                        value={(sub.credits.used / Math.max(1, sub.credits.limit)) * 100}
                        size={ProgressSize.sm}
                        measureLocation={ProgressMeasureLocation.none}
                        aria-label="Campaign credits used"
                    />
                </div>
                <div style={styles.usageRow}>
                    <div style={styles.usageLabel}>
                        <span>
                            {sub.aiAds.used} / {aiUnlimited ? '∞' : aiLimit} AI ads used
                            {(sub.aiAds.purchased ?? 0) > 0 && (
                                <span style={{ color: '#059669', fontWeight: 600 }}> · +{sub.aiAds.purchased} purchased</span>
                            )}
                        </span>
                    </div>
                    {aiNotIncluded ? (
                        <div style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>
                            Not included in your tier
                        </div>
                    ) : aiUnlimited ? (
                        <div style={{ fontSize: '13px', color: '#10b981', fontWeight: 600 }}>
                            Unlimited
                        </div>
                    ) : (
                        <Progress
                            value={(sub.aiAds.used / Math.max(1, aiLimit!)) * 100}
                            size={ProgressSize.sm}
                            measureLocation={ProgressMeasureLocation.none}
                            aria-label="AI ads used"
                        />
                    )}
                </div>
            </div>
        );
    };

    const tierButton = (tier: TierResponse) => {
        if (!currentTier) {
            return (
                <Button variant="primary" onClick={() => handleSubscribe(tier.tier)} isDisabled={busy} isBlock>
                    Subscribe
                </Button>
            );
        }
        if (tier.tier === currentTier) {
            return (
                <Button variant="secondary" isDisabled isBlock>
                    Current
                </Button>
            );
        }
        const isUpgrade = tierRank(tier.tier) > tierRank(currentTier);
        return (
            <Button
                variant={isUpgrade ? 'primary' : 'secondary'}
                onClick={() => startChange(tier.tier)}
                isDisabled={busy}
                isBlock
            >
                {isUpgrade ? 'Upgrade' : 'Downgrade'}
            </Button>
        );
    };

    const renderTierFeatures = (tier: TierResponse) => {
        const aiLine = tier.aiAdLimit === null
            ? 'AI ads: Unlimited'
            : tier.aiAdLimit === 0
                ? 'AI ads: not included'
                : `AI ads: ${tier.aiAdLimit}/mo`;
        return (
            <ul style={styles.featureList}>
                <li>{tier.creditLimit} campaigns/mo</li>
                <li>{aiLine}</li>
                <li>All campaign types</li>
            </ul>
        );
    };

    return (
        <PageSection style={{ minHeight: '100%', background: '#f8fafc' }}>
            <div style={styles.container}>
                <div style={styles.header}>
                    <h1 style={styles.title}>Plan</h1>
                    <p style={styles.subtitle}>Manage your subscription, credits, and AI usage.</p>
                </div>

                {actionError && <div style={styles.errorBanner}>{actionError}</div>}

                {topUpConfirmed && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        background: '#ecfdf5',
                        border: '1px solid #a7f3d0',
                        color: '#065f46',
                        borderRadius: '10px',
                        padding: '12px 16px',
                        marginBottom: '16px',
                        fontSize: '14px'
                    }}>
                        <CheckCircleIcon style={{ color: '#059669' }} />
                        <span style={{ flex: 1 }}>Top-up confirmed — your credits will appear shortly.</span>
                        <Button variant="plain" aria-label="Dismiss" onClick={() => setTopUpConfirmed(false)} style={{ color: '#065f46' }}>
                            ×
                        </Button>
                    </div>
                )}

                {showTrialBanner && trialOffer && (
                    <div style={styles.trialBanner}>
                        <div style={{ flex: 1, minWidth: '240px' }}>
                            <div style={{ fontSize: '18px', fontWeight: 700, marginBottom: '4px' }}>
                                You've been invited to a free trial!
                            </div>
                            <div style={{ fontSize: '14px', opacity: 0.95 }}>
                                Try {trialOffer.tier.charAt(0) + trialOffer.tier.slice(1).toLowerCase()} free for {trialOffer.trialDays} days. Card required, no charge until trial ends.
                            </div>
                        </div>
                        <Button
                            variant="secondary"
                            onClick={handleStartTrial}
                            isDisabled={busy}
                            style={{ background: '#ffffff', color: '#b45309', fontWeight: 600 }}
                        >
                            Start {trialOffer.trialDays}-day {trialOffer.tier.charAt(0) + trialOffer.tier.slice(1).toLowerCase()} trial
                        </Button>
                    </div>
                )}

                {!hasActiveSub && (
                    <div style={styles.sectionCard}>
                        <div style={styles.sectionTitle}>One-off credits</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                            <div style={{ flex: 1, minWidth: '240px' }}>
                                <p style={{ margin: 0, color: '#4b5563', fontSize: '14px', lineHeight: 1.6 }}>
                                    No subscription needed — buy campaign or AI ad credits individually. They never expire.
                                </p>
                                {((subscription?.credits?.purchased ?? 0) > 0 || (subscription?.aiAds?.purchased ?? 0) > 0) && (
                                    <p style={{ margin: '6px 0 0', color: '#059669', fontSize: '13px', fontWeight: 600 }}>
                                        You have
                                        {(subscription?.credits?.purchased ?? 0) > 0 && <> {subscription!.credits.purchased} campaign credits</>}
                                        {(subscription?.credits?.purchased ?? 0) > 0 && (subscription?.aiAds?.purchased ?? 0) > 0 && <> and</>}
                                        {(subscription?.aiAds?.purchased ?? 0) > 0 && <> {subscription!.aiAds.purchased} AI ad credits</>}
                                        {' '}available.
                                    </p>
                                )}
                            </div>
                            <Button variant="primary" onClick={() => setTopUp('plan')} isDisabled={busy}>
                                Buy credits
                            </Button>
                        </div>
                    </div>
                )}

                {hasSubscription && subscription && subscription.tier && (() => {
                    const theme = tierTheme(subscription.tier);
                    return (
                    <div style={{ ...styles.sectionCard, borderLeft: `4px solid ${theme.border}` }}>
                        <div style={styles.sectionTitle}>Current Plan</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '16px', marginBottom: '16px' }}>
                            <div>
                                <div style={{ fontSize: '24px', fontWeight: 700, color: theme.accent, display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '50%',
                                        background: theme.gradient,
                                        color: '#ffffff',
                                        fontSize: '18px',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                    }}>
                                        {subscription.tier === 'GOLD' ? <CrownIcon /> : subscription.tier === 'SILVER' ? <StarIcon /> : <BoltIcon />}
                                    </span>
                                    {subscription.tier.charAt(0) + subscription.tier.slice(1).toLowerCase()}
                                </div>
                                {(() => {
                                    const tierCfg = tiers.find((t) => t.tier === subscription.tier);
                                    return tierCfg ? (
                                        <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                                            {formatPrice(tierCfg.priceCents)} / month
                                        </div>
                                    ) : null;
                                })()}
                                {subscription.pendingTier && (
                                    <div style={{ fontSize: '12px', color: '#92400e', marginTop: '6px' }}>
                                        Downgrading to {subscription.pendingTier} on {formatDate(subscription.currentPeriodEnd)}
                                    </div>
                                )}
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                {renderStatusBadge(subscription)}
                                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '8px' }}>
                                    {subscription.status === 'INCOMPLETE' ? (
                                        'Awaiting payment confirmation'
                                    ) : subscription.status === 'TRIALING' && subscription.trialEnd ? (
                                        (() => {
                                            const tierCfg = tiers.find((t) => t.tier === subscription.tier);
                                            return tierCfg
                                                ? `Trial converts on ${formatDate(subscription.trialEnd)} — you'll be charged ${formatPrice(tierCfg.priceCents)}`
                                                : `Trial converts on ${formatDate(subscription.trialEnd)}`;
                                        })()
                                    ) : (
                                        `Renews on ${formatDate(subscription.currentPeriodEnd)}`
                                    )}
                                </div>
                            </div>
                        </div>

                        {subscription.status !== 'INCOMPLETE' && renderUsageBars(subscription)}

                        <div style={{ display: 'flex', gap: '8px', marginTop: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                            {hasActiveSub && (
                                <Button variant="secondary" onClick={() => setTopUp('plan')} isDisabled={busy}>
                                    Buy credits
                                </Button>
                            )}
                            {subscription.status === 'INCOMPLETE' ? (
                                <>
                                    <Button
                                        variant="primary"
                                        onClick={() => handleSubscribe(subscription.tier!)}
                                        isDisabled={busy}
                                    >
                                        Complete payment
                                    </Button>
                                    <Button variant="link" onClick={() => setConfirmingCancel(true)} isDisabled={busy}>
                                        Cancel
                                    </Button>
                                </>
                            ) : subscription.cancelAtPeriodEnd ? (
                                <Button variant="primary" onClick={handleReactivate} isDisabled={busy}>
                                    Reactivate
                                </Button>
                            ) : (
                                <Button variant="link" onClick={() => setConfirmingCancel(true)} isDisabled={busy}>
                                    Cancel subscription
                                </Button>
                            )}
                        </div>
                    </div>
                    );
                })()}

                {hasSubscription && subscription?.status !== 'INCOMPLETE' && <PaymentMethodsSection />}
                {/* Always mounted: plan-less users get invoices for one-off credit top-ups too.
                    It renders nothing when there is no billing history yet. */}
                <InvoicesSection
                    // Remount (and refetch) when a top-up lands or the subscription status flips,
                    // so the new invoice shows without a page reload.
                    key={`${subscription?.status}-${subscription?.credits?.purchased ?? 0}-${subscription?.aiAds?.purchased ?? 0}`}
                    hideWhenEmpty={!(hasSubscription && subscription?.status !== 'INCOMPLETE')}
                />


                <div style={styles.sectionCard}>
                    <div style={styles.sectionTitle}>
                        {currentTier ? 'Change plan' : 'Choose a plan'}
                    </div>
                    {tiers.length === 0 ? (
                        <p style={{ color: '#6b7280', fontSize: '14px' }}>Plans are loading…</p>
                    ) : (
                        <div style={styles.tierGrid}>
                            {tiers.map((tier) => {
                                const isCurrent = currentTier === tier.tier;
                                const theme = tierTheme(tier.tier);
                                return (
                                    <div
                                        key={tier.tier}
                                        style={{
                                            ...styles.tierCard,
                                            borderColor: isCurrent ? theme.border : '#e5e7eb',
                                            ...(isCurrent
                                                ? { background: theme.softBg, boxShadow: theme.shadow }
                                                : {})
                                        }}
                                    >
                                        <div style={{ ...styles.tierStripe, background: theme.gradient }} />
                                        <div style={styles.tierName}>
                                            <span style={{ ...styles.tierIconCircle, background: theme.gradient }}>
                                                {tier.tier === 'GOLD' ? <CrownIcon /> : tier.tier === 'SILVER' ? <StarIcon /> : <BoltIcon />}
                                            </span>
                                            <span style={{ color: theme.accent }}>{tier.displayName}</span>
                                            {isCurrent && (
                                                <span style={{
                                                    ...styles.badge,
                                                    background: '#ffffff',
                                                    color: theme.accent,
                                                    border: `1px solid ${theme.border}`,
                                                    marginLeft: 'auto'
                                                }}>
                                                    Current
                                                </span>
                                            )}
                                        </div>
                                        <div>
                                            <span style={styles.tierPrice}>{formatPrice(tier.priceCents)}</span>
                                            <span style={styles.tierPriceSuffix}> / month</span>
                                        </div>
                                        {renderTierFeatures(tier)}
                                        {tierButton(tier)}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {pendingChange && subscription && subscription.tier && (() => {
                const isUpgrade = tierRank(pendingChange) > tierRank(subscription.tier);
                const isTrialUpgrade = isUpgrade && (subscription.status === 'TRIALING' || preview?.changeType === 'UPGRADE_TRIAL');
                const needsCard = !!preview?.requiresPaymentMethod;

                let body: React.ReactNode;
                if (!isUpgrade) {
                    body = (
                        <span>
                            Your plan will switch at the end of your current billing cycle ({formatDate(subscription.currentPeriodEnd)}). You'll keep your current quotas until then.
                        </span>
                    );
                } else if (isTrialUpgrade) {
                    body = (
                        <span>
                            Your plan switches immediately and you get the new credits / AI quota right away. You won't be charged during your trial — the new {pendingChange} rate applies when your trial converts{subscription.trialEnd ? ` on ${formatDate(subscription.trialEnd)}` : ''}.
                        </span>
                    );
                } else if (previewLoading) {
                    body = (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            <Spinner size="md" /> Calculating your prorated charge…
                        </span>
                    );
                } else if (needsCard) {
                    body = (
                        <span>
                            You don't have a payment method on file. Add a card in the <strong>Payment methods</strong> section below, then come back to upgrade.
                        </span>
                    );
                } else if (preview) {
                    body = (
                        <span>
                            You'll be charged <strong>{formatMoney(preview.immediateChargeCents)}</strong> today — the prorated difference for the rest of this billing period — and your new credits / AI quota are available right away.
                            {preview.nextRenewalDate ? ` Your next renewal on ${formatDate(preview.nextRenewalDate)} will bill the full ${formatPrice(preview.targetPriceCents)}/mo.` : ''}
                        </span>
                    );
                } else {
                    body = <span>Your plan will switch immediately.</span>;
                }

                const bodyWithError = (
                    <>
                        {body}
                        {actionError && (
                            <div style={{ marginTop: 12, color: '#b91c1c', fontWeight: 500 }}>{actionError}</div>
                        )}
                    </>
                );

                return (
                    <ConfirmModal
                        title={isUpgrade ? `Upgrade to ${pendingChange}?` : `Downgrade to ${pendingChange}?`}
                        body={bodyWithError}
                        confirmLabel={isUpgrade ? 'Upgrade' : 'Schedule downgrade'}
                        busy={busy || previewLoading}
                        onConfirm={handleConfirmChange}
                        onCancel={closeChange}
                    />
                );
            })()}

            {confirmingCancel && subscription && subscription.tier && (
                <ConfirmModal
                    title="Cancel subscription?"
                    body={
                        <span>
                            You'll keep {subscription.tier.charAt(0) + subscription.tier.slice(1).toLowerCase()} access until {formatDate(subscription.currentPeriodEnd)}. After that, campaign activation and AI ad generation will be blocked until you resubscribe.
                        </span>
                    }
                    confirmLabel="Cancel subscription"
                    danger
                    busy={busy}
                    onConfirm={handleCancel}
                    onCancel={() => setConfirmingCancel(false)}
                />
            )}

            <SubscribePaymentModal
                isOpen={!!payment}
                checkout={payment?.checkout ?? null}
                tierLabel={payment?.tierLabel ?? ''}
                finalizing={postPaymentPolling}
                onClose={() => !postPaymentPolling && setPayment(null)}
                onSuccess={handlePaymentSuccess}
            />

            <TopUpModal
                isOpen={topUp !== null}
                context={topUp ?? 'plan'}
                onClose={() => setTopUp(null)}
                onPurchased={(sub) => {
                    if (sub) setSubscription(sub);
                }}
            />
        </PageSection>
    );
};

export { PlanComponent };
